import React from 'react'

import { Spinner } from '@components/atoms'

import { useNavigate } from 'react-router-dom'
import {
	useAppSelector,
	useAppDispatch,
	customerAuthActions,
	routeActions,
} from '@redux/index'
import {
	customerRoleRequiredByPath,
	customerRoleRequiredFor,
	POST_LOGIN_REDIRECT_KEY,
} from '@configs'

interface PropsType {
	to?: string
	isShouldLoginPage?: boolean
	children: JSX.Element
}

const EMPTY_ROLES: string[] = []

export const CustomerLoginGuard: React.FC<PropsType> = props => {
	// redux dispatch
	const reduxDispatch = useAppDispatch()

	// redux state
	const currentPath: string = useAppSelector(state => state.route.currentPath)
	const isLogedin: boolean = useAppSelector(state => state.customerAuth.isLogedin)
	const isReduxLoading: boolean = useAppSelector(state => state.customerAuth.isLoading)
	// 옛 세션이 되살린 customer 에는 roles 가 없을 수 있다 — 고정 빈 배열로 받아 effect 가 매 렌더 돌지 않게 한다
	const customerRoles: string[] = useAppSelector(
		state => state.customerAuth.customer.roles ?? EMPTY_ROLES,
	)

	const authWhiteList = [
		'/customer/info',
		'/customer/order',
		...Object.keys(customerRoleRequiredByPath),
	]
	const authBlackList = ['/customer/login', '/customer/join', '/customer/forgot-password']

	/**
	 * My Inquiries 대화(/customer/inquiry/:id)는 답변 메일의 링크로 로그아웃 상태에서 들어온다.
	 * 옛 주소 /customer/messages/… 도 같은 대접을 한다(로그인 뒤 라우트가 새 주소로 넘긴다).
	 * 홈으로 보내지 않고 로그인 페이지로 보낸 뒤 그 대화로 돌아오게 한다(포럼 SSO 와 같은 키).
	 */
	const isInquiryPath: boolean = ['/customer/inquiry', '/customer/messages'].some(
		aPath => currentPath === aPath || currentPath.startsWith(`${aPath}/`),
	)

	React.useEffect(() => {
		reduxDispatch(customerAuthActions.IS_LOADING(true))
		reduxDispatch(customerAuthActions.TRIGGER_TOKEN_VALIDATION(true))
	}, [])

	React.useEffect(() => {
		if (!isReduxLoading) {
			if (isLogedin) {
				if (authBlackList.includes(currentPath)) {
					reduxDispatch(routeActions.NAVIGATE_PATH('/'))
				} else {
					// 역할이 필요한 계정 페이지(Wholesale Orders · Performance). 역할은 토큰이 아니라
					// 토큰검증 응답에 실려 오므로 isLoading 이 끝난 뒤에만 판정한다 (PARTNER_PLAN §2.3).
					// 하위 주소(/customer/wholesale/:orderId)도 같은 역할을 본다
					const requiredRoles: string[] | undefined = customerRoleRequiredFor(currentPath)

					if (
						requiredRoles !== undefined &&
						!requiredRoles.some(aRole => customerRoles.includes(aRole))
					) {
						reduxDispatch(routeActions.NAVIGATE_PATH('/customer/info'))
					}
				}
			} else if (!isLogedin) {
				if (isInquiryPath) {
					try {
						sessionStorage.setItem(POST_LOGIN_REDIRECT_KEY, currentPath)
					} catch (error) {
						// 저장소가 막혀 있으면 로그인 뒤 홈으로 간다
					}
					reduxDispatch(routeActions.NAVIGATE_PATH('/customer/login'))
				} else if (
					// 하위 주소(/customer/order/:orderId 등)도 같은 대접
					authWhiteList.some(
						aPath => currentPath === aPath || currentPath.startsWith(`${aPath}/`),
					) ||
					customerRoleRequiredFor(currentPath) !== undefined
				) {
					reduxDispatch(routeActions.NAVIGATE_PATH('/'))
				}
			}
		}
	}, [currentPath, isLogedin, isReduxLoading, customerRoles])

	return <>{isReduxLoading ? <Spinner /> : props.children}</>
}
